import React from "react";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { useDownloadFolderContent } from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { MemberAvatar } from "@common/memberAvatar";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { TransferIcon } from "@common/transferIcon";
import { DownloadIcon } from "@common/assets/downloadIcon";
import { TrashIcon } from "@common/trashIcon";
import { Loader } from "@common/loader";
import { PenIcon } from "@common/penIcon";
import { FolderIcon } from "../../folderIcon";
import { Props } from "./types";
import styles from "./styles.scss";

function Folder({ project, folder }: Props) {
  const { t } = useTranslation();
  const push = useNavigate();
  const { pushView } = useModal();
  const { downloadFolderContent, isRequestPending } =
    useDownloadFolderContent(folder);

  return (
    <div
      className={styles.folder}
      onDoubleClick={() =>
        push(`/projects/${project.uuid}/documents/folders/${folder.uuid}`)
      }
    >
      <div>
        <FolderIcon className={styles.icon} />
        <div className={styles.column}>
          <p className={styles.name}>{folder.name}</p>
          <p className={styles.childrenNbr}>
            {folder.children.length} éléments
          </p>
        </div>
      </div>
      <div>
        <div className={styles.avatar}>
          <MemberAvatar name={folder.createdBy} avatarUrl="" />
        </div>
      </div>
      <div>
        <p className={styles.creationDate}>
          {new Date(folder.createdDate).toLocaleDateString()}
        </p>
      </div>
      {isRequestPending ? (
        <div className={styles.loader}>
          <div>
            <Loader />
          </div>
        </div>
      ) : (
        <div className={styles.contextualMenu}>
          <SecureContextualMenu
            items={[
              {
                name: t("projectPage.documentsPage.documentsPage.download"),
                useCase: UseCases.downloadFolderContent,
                renderIcon: (className) => (
                  <DownloadIcon className={className} />
                ),
                act: () => downloadFolderContent({ folder, saveToDisk: true }),
              },
              {
                name: t("projectPage.documentsPage.documentsPage.rename"),
                useCase: UseCases.updateFolder,
                renderIcon: (className) => <PenIcon className={className} />,
                act: () =>
                  pushView({
                    key: UseCases.updateFolder,
                    data: {
                      folder,
                      type: "project",
                      namespace: project.uuid,
                    },
                  }),
              },
              {
                name: t("projectPage.documentsPage.documentsPage.move"),
                useCase: UseCases.moveFolder,
                renderIcon: (className) => (
                  <TransferIcon className={className} />
                ),
                act: () =>
                  pushView({
                    key: UseCases.moveFolder,
                    data: {
                      folder,
                      namespace: project.uuid,
                      type: "project",
                      projectName: project.name,
                    },
                  }),
              },
              {
                name: t("projectPage.documentsPage.documentsPage.remove"),
                useCase: UseCases.removeFolder,
                color: "#db5f63",
                renderIcon: (className) => (
                  <TrashIcon
                    className={classNames(className, styles.removeIcon)}
                  />
                ),
                act: () =>
                  pushView({
                    key: UseCases.removeFolder,
                    data: folder,
                  }),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}

export { Folder };
